import { useEffect, useState, useRef } from 'react';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Calendar, Lock, Edit2 } from 'lucide-react';
import { formatDate } from '@/lib/utils/date';
import { Post } from '@/lib/firebase/posts';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import SupportPopup from '@/components/ui/SupportPopup';
import CommentsSection from '@/components/comments/CommentsSection';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { Heart } from 'lucide-react';
import { toggleLike, checkIfLiked } from '@/lib/firebase/posts';
import { subscribeToComments } from '@/lib/firebase/comments/queries';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';

interface PostPreviewCardProps {
    post: Post;
    user: any;
    onPostUpdated?: (updatedPost: Post) => void;
    creatorName?: string;
    isSubscribed?: boolean;
}

const PREVIEW_CHAR_LIMIT = 180;

export default function PostPreviewCard({ post, user, onPostUpdated, creatorName, isSubscribed }: PostPreviewCardProps) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [showSupportPopup, setShowSupportPopup] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const membershipRef = useRef<HTMLDivElement | null>(null);
    const navigate = useNavigate();
    const [allowEngagement, setAllowEngagement] = useState<boolean>(true);
    const [isLiked, setIsLiked] = useState(false);
    const [isLiking, setIsLiking] = useState(false);
    const [likeCount, setLikeCount] = useState<number>(post.stats?.likes || 0);
    const [commentCount, setCommentCount] = useState<number>(0);

    // Determine access
    const isOwner = user?.uid === post.userId;
    const isMembersOnly = post.publishingOptions?.access === 'members';
    const isPaid = post.publishingOptions?.access === 'paid_members';
    const hasAccess = isOwner || (!isPaid && !isMembersOnly) || (isSubscribed && (isMembersOnly || isPaid)) || !post.isLocked;

    // Header image
    const headerImage = post.images && post.images.length > 0 ? post.images[0] : null;
    const otherImages = post.images && post.images.length > 1 ? post.images.slice(1) : [];

    // Prefer uploaded video over YouTube URL
    const uploadedVideo = (post as any).videoUpload as string | undefined;
    const youtubeUrl = post.videoUrl;
    let youtubeEmbed = null;
    if (!uploadedVideo && youtubeUrl) {
        const match = youtubeUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
        const videoId = match ? match[1] : null;
        if (videoId) {
            youtubeEmbed = (
                <div className="my-4">
                    <iframe
                        className="w-full aspect-video rounded-lg"
                        src={`https://www.youtube.com/embed/${videoId}`}
                        title="YouTube video"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                </div>
            );
        }
    }

    // Preview HTML (preserve bullets, etc.)
    function getPreviewHtml(html: string, maxLen = PREVIEW_CHAR_LIMIT) {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        let charCount = 0;
        function walk(node: Node): Node | null {
            if (charCount >= maxLen) return null;
            if (node.nodeType === Node.TEXT_NODE) {
                const text = node.textContent || '';
                if (charCount + text.length > maxLen) {
                    const span = document.createElement('span');
                    span.textContent = text.slice(0, maxLen - charCount);
                    charCount = maxLen;
                    return span;
                } else {
                    charCount += text.length;
                    return node.cloneNode();
                }
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as Element;
                const clone = el.cloneNode(false) as Element;
                for (const child of Array.from(el.childNodes)) {
                    const childClone = walk(child);
                    if (childClone) clone.appendChild(childClone);
                    if (charCount >= maxLen) break;
                }
                return clone;
            }
            return null;
        }
        const previewNodes = Array.from(tmp.childNodes).map(walk).filter(Boolean);
        const previewDiv = document.createElement('div');
        previewNodes.forEach(n => n && previewDiv.appendChild(n));
        return previewDiv.innerHTML;
    }

    // Scroll to the donate section and open the donation form.
    // Membership tiers were removed from the product; this used to target
    // #membership-tiers, an element that no longer exists anywhere, so the
    // "See Support Options" popup silently did nothing on click (reported by
    // a real visitor on the Destiny Alanis mission page, 2026-09-24).
    function scrollToSupport() {
        const el = document.getElementById('donate');
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        window.dispatchEvent(new CustomEvent('open-donate', { detail: { surface: 'post_preview' } }));
    }

    // Determine long-text regardless of expanded state
    const isLongText = !!(post.content && post.content.replace(/<[^>]+>/g, '').length > PREVIEW_CHAR_LIMIT);
    // Fade out overlay logic
    const needsFade = !expanded && isLongText;

    // Check if there's content to expand (long text, YouTube/Uploaded video, or additional images)
    const hasExpandableContent = isLongText || !!youtubeEmbed || otherImages.length > 0;

    const publishStatus = post.isPublished ? t('posts.card.published') : t('posts.card.draft');
    const publishColor = post.isPublished ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';

    // Load creator engagement flag
    useEffect(() => {
        async function loadEngagement() {
            try {
                const snap = await getDoc(doc(db, 'users', post.userId));
                if (snap.exists()) setAllowEngagement(snap.data().allowEngagement ?? true);
            } catch (_) { }
        }
        loadEngagement();
    }, [post.userId]);

    useEffect(() => {
        setLikeCount(post.stats?.likes || 0);
    }, [post.stats?.likes]);

    useEffect(() => {
        async function loadLiked() {
            if (!user?.uid) return;
            try {
                const liked = await checkIfLiked(post.id, user.uid);
                setIsLiked(liked);
            } catch (_) { }
        }
        loadLiked();
    }, [post.id, user?.uid]);

    // Subscribe to comments to keep a live count, but only render comments when expanded
    useEffect(() => {
        let unsub: (() => void) | null = null;
        try {
            unsub = subscribeToComments(
                post.id,
                { includePrivateForUserId: user?.uid ?? undefined },
                (list) => setCommentCount(list.length),
                () => { }
            );
        } catch (_) { }
        return () => { if (unsub) unsub(); };
    }, [post.id, user?.uid]);

    async function handleLike() {
        if (!user?.uid) {
            setShowSupportPopup(true);
            return;
        }
        if (isLiking) return;
        setIsLiking(true);
        try {
            const { liked } = await toggleLike(post.id, user.uid);
            setIsLiked(liked);
            setLikeCount((c) => c + (liked ? 1 : -1));
        } catch (e) {
            // ignore
        } finally {
            setIsLiking(false);
        }
    }

    return (
        <>
            <Card className="overflow-hidden">
                <Card.Content className="relative p-0">
                    {/* Edit button for post owners */}
                    {isOwner && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="absolute top-3 right-3 z-10 bg-white/90 hover:bg-white"
                            onClick={() => navigate(`/creator/content/${post.id}/edit`)}
                        >
                            <Edit2 className="h-4 w-4 mr-2" /> {t('posts.card.edit')}
                        </Button>
                    )}
                    {/* Header image */}
                    {headerImage && (
                        <div className="relative">
                            <img
                                src={headerImage}
                                alt="Header"
                                className="w-full h-56 object-cover"
                            />
                            {isPaid && (
                                <div className="absolute top-3 left-3 flex items-center gap-1 bg-black/60 text-white px-2 py-1 rounded text-xs">
                                    <Lock className="w-4 h-4 mr-1" /> {t('posts.card.locked')}
                                </div>
                            )}
                        </div>
                    )}
                    <div className="p-6">
                        <div className="flex items-center gap-3 mb-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-500 text-sm">
                                {post.createdAt?.toDate ? formatDate(post.createdAt.toDate()) : t('posts.card.justNow')}
                            </span>
                            <Badge className={(isPaid || isMembersOnly) ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}>
                                {isPaid ? t('posts.card.paidMembersOnly') : isMembersOnly ? t('posts.card.membersOnly') : t('posts.card.public')}
                            </Badge>
                            <Badge className={publishColor}>{publishStatus}</Badge>
                        </div>
                        <h2 className="text-xl font-bold mb-2 text-gray-900">{post.title}</h2>
                        {/* Content preview or full content */}
                        {hasAccess ? (
                            <div className="relative">
                                <div
                                    ref={contentRef}
                                    className={clsx(
                                        'transition-all duration-500 overflow-hidden',
                                        expanded ? 'max-h-[2000px] opacity-100' : 'max-h-32 opacity-90'
                                    )}
                                >
                                    <div
                                        className="text-base text-gray-800 mb-3 prose prose-base max-w-none"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(expanded ? post.content : getPreviewHtml(post.content)) }}
                                    />
                                    {/* Uploaded video or YouTube embed */}
                                    {expanded && (
                                        uploadedVideo ? (
                                            <div className="my-4">
                                                <video className="w-full aspect-video rounded-lg" src={uploadedVideo} controls />
                                            </div>
                                        ) : youtubeEmbed
                                    )}
                                    {/* Image carousel */}
                                    {expanded && otherImages.length > 0 && (
                                        <div className="mb-6">
                                            <Swiper
                                                modules={[Navigation, Pagination]}
                                                navigation
                                                pagination={{ clickable: true }}
                                                spaceBetween={16}
                                                slidesPerView={1}
                                                className="rounded-lg"
                                            >
                                                {otherImages.map((image, index) => (
                                                    <SwiperSlide key={index}>
                                                        <img
                                                            src={image}
                                                            alt={`Post image ${index + 2}`}
                                                            className="w-full max-h-96 rounded-lg object-cover cursor-pointer"
                                                        />
                                                    </SwiperSlide>
                                                ))}
                                            </Swiper>
                                        </div>
                                    )}
                                </div>
                                {/* Comments inside card when expanded */}
                                {allowEngagement && expanded && (
                                    <div className="mt-6">
                                        <CommentsSection post={post} allowEngagement={allowEngagement} viewerRole={isOwner ? 'creator' : 'supporter'} />
                                    </div>
                                )}
                                {/* Fade overlay for preview */}
                                {!expanded && needsFade && (
                                    <div className="absolute left-0 right-0 bottom-8 h-12 pointer-events-none bg-gradient-to-t from-white/90 to-transparent transition-all duration-500" />
                                )}
                                {/* Read more row when collapsed */}
                                {hasExpandableContent && !expanded && (
                                    <div className="flex items-center justify-between mt-2">
                                        <button
                                            className="text-primary hover:underline ml-1 font-medium"
                                            onClick={() => setExpanded(true)}
                                        >
                                            {t('posts.card.readMore')}
                                        </button>
                                        <div className="flex items-center gap-4 text-sm">
                                            <button
                                                onClick={handleLike}
                                                disabled={isLiking}
                                                className={`flex items-center gap-1 ${isLiked ? 'text-red-500' : 'text-gray-500'} hover:text-red-500`}
                                            >
                                                <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} />
                                                <span>{likeCount}</span>
                                            </button>
                                            {commentCount > 0 && (
                                                <span className="text-gray-600">{commentCount} {commentCount === 1 ? t('posts.card.comment') : t('posts.card.comments')}</span>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {/* Show less row when expanded */}
                                {hasExpandableContent && expanded && (
                                    <div className="flex items-center justify-between mt-2">
                                        <button
                                            className="text-primary hover:underline ml-1 font-medium"
                                            onClick={() => setExpanded(false)}
                                        >
                                            {t('posts.card.showLess')}
                                        </button>
                                        <div className="flex items-center gap-4 text-sm">
                                            <button
                                                onClick={handleLike}
                                                disabled={isLiking}
                                                className={`flex items-center gap-1 ${isLiked ? 'text-red-500' : 'text-gray-500'} hover:text-red-500`}
                                            >
                                                <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} />
                                                <span>{likeCount}</span>
                                            </button>
                                            {commentCount > 0 && (
                                                <span className="text-gray-600">{commentCount} {commentCount === 1 ? t('posts.card.comment') : t('posts.card.comments')}</span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="text-base text-gray-800 mb-3 prose prose-base max-w-none"
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getPreviewHtml(post.content)) }}
                                />
                                <Button
                                    variant="secondary"
                                    className="flex items-center gap-2"
                                    onClick={() => setShowSupportPopup(true)}
                                >
                                    <Lock className="w-4 h-4" /> {t('posts.card.unlockPost')}
                                </Button>
                            </>
                        )}
                    </div>
                </Card.Content>
            </Card>

            <SupportPopup
                isOpen={showSupportPopup}
                onClose={() => setShowSupportPopup(false)}
                onSupportClick={scrollToSupport}
                creatorName={creatorName || 'this creator'}
            />


        </>
    );
} 